import Swiper from "swiper";
import { Controller, EffectFade, Navigation, Pagination } from "swiper/modules";
import GLightbox from "glightbox";

document.addEventListener('DOMContentLoaded', () => {
  // Fixed scroll behavior for .pd__scroll
  const pdScroll = document.querySelector(".pd__scroll");
  const pdHero = document.querySelector(".pd__hero");
  const pdElement = document.querySelector(".pd");

  if (pdScroll && pdHero && pdElement) {
    let scrollTimeout;
    let isFixed = false;
    let placeholderElement = null;
    let savedLeft = 0;

    // Create placeholder element to replace pdScroll when fixed
    const createPlaceholder = () => {
      if (!placeholderElement) {
        placeholderElement = document.createElement('div');
        placeholderElement.className = 'pd__scroll-placeholder';
        placeholderElement.style.visibility = 'hidden';
        placeholderElement.style.pointerEvents = 'none';
      }
      return placeholderElement;
    };

    // Function to handle scroll position and fix/unfix element
    const handleScroll = () => {
      if (window.innerWidth < 1280) { // xl breakpoint
        if (isFixed) {
          pdScroll.classList.remove('is-fixed');
          pdScroll.style.left = '';
          if (placeholderElement && placeholderElement.parentNode) {
            placeholderElement.parentNode.removeChild(placeholderElement);
          }
          isFixed = false;
        }
        return;
      }

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const pdHeroRect = pdHero.getBoundingClientRect();
      const pdHeroBottom = pdHeroRect.bottom + scrollTop;
      const scrollTriggerPoint = pdHeroBottom;

      if (scrollTop >= scrollTriggerPoint && !isFixed) {
        // Capture position before making it fixed
        const scrollRect = pdScroll.getBoundingClientRect();
        savedLeft = scrollRect.left;
        const scrollHeight = pdScroll.offsetHeight;

        // Create placeholder with same height
        const placeholder = createPlaceholder();
        placeholder.style.height = `${scrollHeight}px`;
        placeholder.style.display = 'block';

        // Insert placeholder right before pdScroll to take its place in the flow
        pdScroll.parentNode.insertBefore(placeholder, pdScroll);

        // Make element fixed (this removes it from flow, placeholder takes its space)
        pdScroll.classList.add('is-fixed');
        pdScroll.style.left = `${savedLeft}px`;

        isFixed = true;
      } else if (scrollTop < scrollTriggerPoint && isFixed) {
        // Element should be unfixed
        pdScroll.classList.remove('is-fixed');
        pdScroll.style.left = '';

        // Remove placeholder
        if (placeholderElement && placeholderElement.parentNode) {
          placeholderElement.parentNode.removeChild(placeholderElement);
        }

        isFixed = false;
      }

      // Update placeholder height if fixed and element height changed
      if (isFixed && placeholderElement && placeholderElement.parentNode) {
        const currentScrollHeight = pdScroll.offsetHeight;
        const currentPlaceholderHeight = parseInt(placeholderElement.style.height) || 0;
        if (Math.abs(currentScrollHeight - currentPlaceholderHeight) > 1) {
          placeholderElement.style.height = `${currentScrollHeight}px`;
        }
      }
    };

    // Debounced scroll handler
    const debouncedHandleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 10);
    };

    // Initial check
    handleScroll();

    // Listen for scroll events
    window.addEventListener('scroll', debouncedHandleScroll, { passive: true });

    // Listen for resize events
    window.addEventListener('resize', () => {
      if (!isFixed) {
        // Only update if not fixed, as we'll recalculate on next scroll
        debouncedHandleScroll();
      } else {
        // Recalculate left position when fixed (width stays 100% naturally)
        const scrollRect = pdScroll.getBoundingClientRect();
        savedLeft = scrollRect.left;
        pdScroll.style.left = `${savedLeft}px`;
        debouncedHandleScroll();
      }
    });

    // MutationObserver to watch for changes in pdScroll height
    if ('MutationObserver' in window) {
      const mutationObserver = new MutationObserver(() => {
        if (isFixed) {
          debouncedHandleScroll();
        }
      });

      mutationObserver.observe(pdScroll, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    // ResizeObserver for pdScroll height changes
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        if (isFixed) {
          debouncedHandleScroll();
        }
      });
      resizeObserver.observe(pdScroll);
    }
  }

  // Cache all DOM elements from single base selector
  const gallery = document.querySelector(".pd__gallery");
  if (!gallery) return;

  const mainContainer = gallery.querySelector(".pd__gallery--main");
  const thumbsContainer = gallery.querySelector(".pd__gallery--thumbs");
  const mainSwiper = mainContainer?.querySelector(".swiper");
  const thumbsSwiper = thumbsContainer?.querySelector(".swiper");

  // Media query for 1024px and above
  const mediaQuery = window.matchMedia("(min-width: 1024px)");

  // Function to sync gallery heights
  const syncGalleryHeights = () => {
    if (mediaQuery.matches && mainContainer && thumbsContainer) {
      const mainHeight = mainContainer.offsetHeight;
      thumbsContainer.style.height = `${mainHeight}px`;
    } else if (thumbsContainer) {
      thumbsContainer.style.height = 'auto';
    }
  };

  // Initialize main gallery swiper
  const mainGallerySwiper = new Swiper(mainSwiper, {
    modules: [Controller, EffectFade, Pagination, Navigation],
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    height: "auto",
    pagination: {
      el: ".swiper-pagination",
      type: "bullets",
      clickable: true,
    },
    navigation: {
      nextEl: ".next",
      prevEl: ".previous",
    },
  });

  // Initialize thumbs gallery swiper if exists
  let thumbsGallerySwiper;
  if (thumbsSwiper) {
    thumbsGallerySwiper = new Swiper(thumbsSwiper, {
      modules: [Controller],
      slidesPerView: "auto",
      spaceBetween: 20,
      direction: "vertical",
    });

    // Set up controller synchronization
    thumbsGallerySwiper.controller.control = mainGallerySwiper;
    mainGallerySwiper.controller.control = thumbsGallerySwiper;

    // Thumb slide click handlers
    thumbsGallerySwiper.slides.forEach((slide, index) => {
      slide.addEventListener("click", () => {
        mainGallerySwiper.slideTo(index);
      });
    });

    // Sync active states on slide change
    mainGallerySwiper.on("slideChange", () => {
      const activeIndex = mainGallerySwiper.activeIndex;
      thumbsGallerySwiper.slides.forEach((slide, index) => {
        slide.classList.toggle("active", index === activeIndex);
      });
    });
  }

  // Height synchronization setup
  syncGalleryHeights();
  mediaQuery.addEventListener('change', syncGalleryHeights);
  window.addEventListener('resize', syncGalleryHeights);

  // ResizeObserver for main gallery height changes
  if (mainContainer && 'ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(syncGalleryHeights);
    resizeObserver.observe(mainContainer);
  }

  // Image load event listeners
  const images = mainContainer?.querySelectorAll('img');
  images?.forEach(img => {
    img.addEventListener('load', syncGalleryHeights);
  });

  // Initialize GLightbox for gallery images
  GLightbox({
    selector: '.glightbox',
    touchNavigation: true,
    loop: true,
    autoplayVideos: true,
    plyr: {
      config: {
        ratio: '16:9',
        youtube: {
          noCookie: true,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3
        },
        vimeo: {
          byline: false,
          portrait: false,
          title: false,
          speed: true,
          transparent: false
        }
      }
    }
  });


  // FEATURES ADD TO CART - Handled by woocommerce.js

});
